import { Line } from '@react-three/drei';
import React, { useMemo } from 'react';
import * as THREE from 'three';

import { MeasureKind, UnitsByMeasure } from 'convert';
import { useIntl } from 'react-intl';
import { EUnitType } from '../../../@types/Enums';
import { useLanguage } from '../../../provider/LanguageProvider';
import { useUnitContext } from '../../../provider/UnitProvider';
import {
    DEFAULT_INDICATOR_COLOR,
    DEFAULT_INDICATOR_FONT_SIZE_XS,
    DEFAULT_INDICATOR_LINE_WIDTH,
    UNIT_TO_METER_DIVISOR
} from '../../../utils/constants';
import { convertTo, getLocalizedUnitLabel } from '../../../utils/conversionUtils';
import { localizeNumber } from '../../../utils/numberUtils';
import RENDER_ORDERS from '../../../utils/renderOrder';
import RoundedBoxWithText from '../RoundedBoxWithText';

interface RadiatorLengthIndicatorProps {
    radiatorWidth: number;
    rotation: number; // Added rotation prop
    color?: string;
    lineWidth?: number;
}

const RadiatorLengthIndicator = ({
    radiatorWidth,
    rotation,
    color = DEFAULT_INDICATOR_COLOR,
    lineWidth = DEFAULT_INDICATOR_LINE_WIDTH
}: RadiatorLengthIndicatorProps): React.JSX.Element => {
    const { locale } = useLanguage();
    const { unitType } = useUnitContext();
    const intl = useIntl();
    const mLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, "meter"), [intl]);
    const ftLocalizedLabel = useMemo(() => getLocalizedUnitLabel(intl, "foot"), [intl]);

    const lengthText = useMemo(() => {
        const lengthInMeters = radiatorWidth / UNIT_TO_METER_DIVISOR;
        
        if (unitType === EUnitType.METRIC) {
            return localizeNumber(lengthInMeters.toFixed(2), locale) + mLocalizedLabel;
        }
        
        const lengthInFeet = convertTo(
            lengthInMeters, 
            "meters" as UnitsByMeasure<MeasureKind.Length>, 
            "feet" as UnitsByMeasure<MeasureKind.Length>
        );
        
        return localizeNumber(lengthInFeet.toFixed(2), locale) + ftLocalizedLabel;
    
    }, [locale, unitType, radiatorWidth]);
    
    const startPoint = new THREE.Vector3(-radiatorWidth / 2, 0, 0);
    const endPoint = new THREE.Vector3(radiatorWidth / 2, 0, 0);

    return (
        <group
            name={"group-radiatorLengthIndicator"}
            position={[0, 0, 1]}
            renderOrder={RENDER_ORDERS.lengthIndicator}
        >
            {/* Radiator length indicator line */}
            <Line
                name={"radiatorLengthIndicator-line"}
                points={[startPoint, endPoint]}
                position={[0, 0, -0.5]}
                color={color}
                lineWidth={lineWidth}
            />
            {/* Text group with rotation */}
            <group rotation={[0, 0, -rotation]}>
                <RoundedBoxWithText
                    boxSize={[3, 1, 0]}
                    position={new THREE.Vector3(0, 0, 0)}
                    fontSize={DEFAULT_INDICATOR_FONT_SIZE_XS}
                    textColor="black"
                    text={lengthText}
                />
            </group>
        </group>
    );
};

export default RadiatorLengthIndicator;